// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { ItemFilterState, KnowledgeBaseItem } from '../types'

interface ItemBrowserOptions {
  items: KnowledgeBaseItem[]
  filters: ItemFilterState
  setFilters: (updater: (prev: ItemFilterState) => ItemFilterState) => void
  itemsPerPage?: number
}

export interface ItemBrowserResult {
  filteredItems: KnowledgeBaseItem[]
  paginatedItems: KnowledgeBaseItem[]
  pagination: {
    currentPage: number
    totalPages: number
    setPage: (page: number) => void
  }
  filters: ItemFilterState
  updateText: (text: string) => void
  counts: {
    total: number
    local: number
    shared: number
  }
}

export function useItemBrowser({
  items,
  filters,
  setFilters,
  itemsPerPage = 5,
}: ItemBrowserOptions): ItemBrowserResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = parseInt(searchParams.get('kbpage') ?? '1', 10) || 1

  const filteredItems = useMemo(() => {
    let filtered = [...items].sort((a, b) =>
      b.id.localeCompare(a.id, undefined, { numeric: true, sensitivity: 'base' }),
    )

    if (filters.text.trim()) {
      const searchLower = filters.text.toLowerCase()
      filtered = filtered.filter(item => item.content.toLowerCase().includes(searchLower))
    }

    return filtered
  }, [filters.text, items])

  // Reset page when filter text changes - ref prevents reset on unrelated re-renders
  const filterTextRef = useRef(filters.text)
  useEffect(() => {
    if (filterTextRef.current !== filters.text) {
      filterTextRef.current = filters.text
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('kbpage')
        return next
      }, { replace: true })
    }
  }, [filters.text, setSearchParams])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage))

  // Clamp page to valid range when items change (e.g., after delete or reload)
  useEffect(() => {
    if (currentPage > totalPages) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (totalPages <= 1) {
          next.delete('kbpage')
        } else {
          next.set('kbpage', String(totalPages))
        }
        return next
      }, { replace: true })
    }
  }, [currentPage, totalPages, setSearchParams])

  const effectivePage = Math.min(currentPage, totalPages)

  const paginatedItems = useMemo(() => {
    const startIndex = (effectivePage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredItems.slice(startIndex, endIndex)
  }, [filteredItems, effectivePage, itemsPerPage])

  const updateText = useCallback(
    (text: string) => {
      setFilters(prev => ({
        ...prev,
        text,
      }))
    },
    [setFilters],
  )

  const handleSetPage = useCallback((page: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (page === 1) {
        next.delete('kbpage')
      } else {
        next.set('kbpage', String(page))
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  return {
    filteredItems,
    paginatedItems,
    pagination: {
      currentPage: effectivePage,
      totalPages,
      setPage: handleSetPage,
    },
    filters,
    updateText,
    counts: {
      total: items.length,
      local: items.length,
      shared: 0,
    },
  }
}
